import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentProps,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import type { BodyScrollEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import fileDownload from 'js-file-download';
import { useShallow } from 'zustand/react/shallow';

import { fetchEvaluationExcel } from '@/domains/evaluation/evaluationApi';
import {
  getEvaluationRows,
  getEvaluationTrendData,
  useEvaluationsInfinite,
} from '@/domains/evaluation';
import useApplicationsStore from '@/domains/application/applicationStore';
import RunEvaluation from '@/features/runEvaluation/components/RunEvaluationDialog';
import type { RunEvaluationApplication } from '@/features/runEvaluation/components/RunEvaluationDialog/RunEvaluationDialog.types';
import {
  useMobileRouteLoading,
  useResponsiveViewport,
} from '@/shared/hooks';
import type { Evaluation } from '@/types';

import ApplicationDetailsFields from './ApplicationDetailsFields';
import ApplicationDetailsGrid from './ApplicationDetailsGrid';
import ApplicationDetailsHeader from './ApplicationDetailsHeader';
import ApplicationDetailsMobileList from './ApplicationDetailsMobileList';
import ApplicationDetailsMobilePanel from './ApplicationDetailsMobilePanel';
import ApplicationDetailsToolbar from './ApplicationDetailsToolbar';
import ApplicationDetailsTrend from './ApplicationDetailsTrend';
import {
  getEvaluationReportFilename,
  getRunEvaluationApplication,
  getSelectedEnvironmentAcronym,
} from './ApplicationDetailsPage.utils';

const APPLICATION_DETAILS_OPEN_MOBILE_EVENT =
  'application-details:open-mobile-details';

interface UseApplicationDetailsControllerResult {
  desktopTrendProps: ComponentProps<typeof ApplicationDetailsTrend>;
  fieldsProps: ComponentProps<typeof ApplicationDetailsFields>;
  gridProps: ComponentProps<typeof ApplicationDetailsGrid>;
  headerProps: ComponentProps<typeof ApplicationDetailsHeader>;
  isMobileViewport: boolean;
  mobileListProps: ComponentProps<typeof ApplicationDetailsMobileList>;
  mobilePanelProps: ComponentProps<typeof ApplicationDetailsMobilePanel>;
  runEvaluationProps: ComponentProps<typeof RunEvaluation>;
  toolbarProps: ComponentProps<typeof ApplicationDetailsToolbar>;
}

export const useApplicationDetailsController =
  (): UseApplicationDetailsControllerResult => {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const applicationBitId = params.id;
    const environmentQuery = searchParams.get('env');
    const isMobileViewport = useResponsiveViewport();
    const [selectedEnvironment, setSelectedEnvironment] = useState(
      environmentQuery || 'Production',
    );
    const [isMobileDetailsOpen, setIsMobileDetailsOpen] = useState(false);
    const [activeEvaluationId, setActiveEvaluationId] = useState<
      string | null
    >(null);
    const [isExportingCsv, setIsExportingCsv] = useState(false);
    const [isRunEvaluationOpen, setIsRunEvaluationOpen] = useState(false);
    const gridRef = useRef<AgGridReact<Evaluation>>(null);
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const mobileRunButtonRef = useRef<HTMLButtonElement>(null);
    const mobileLoadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const lastScrollPosition = useRef(0);
    const scrollPositionRef = useRef<{ top: number; left: number }>({
      top: 0,
      left: 0,
    });

    const { applications } = useApplicationsStore(
      useShallow((store) => ({
        applications: store.applications,
      })),
    );

    const application = applications.find(
      (currentApplication) => currentApplication.bitId === applicationBitId,
    );

    const {
      data: evaluationsData,
      fetchNextPage,
      hasNextPage,
      isLoading,
      isFetching,
      isFetchingNextPage,
    } = useEvaluationsInfinite({
      bitId: applicationBitId,
      selectedEnvironment,
    });

    useMobileRouteLoading(isLoading || isFetching);

    useEffect(() => {
      if (environmentQuery) {
        setSelectedEnvironment(environmentQuery);
      }
    }, [environmentQuery]);

    const selectedEnvironmentKey = selectedEnvironment.toLowerCase();
    const selectedEnvironmentAcronym = getSelectedEnvironmentAcronym(
      selectedEnvironment,
    );

    const fetchMoreEvaluations = useCallback(() => {
      if (gridRef.current?.api) {
        const scrollContainer = gridRef.current.api.getVerticalPixelRange();
        scrollPositionRef.current = {
          top: scrollContainer.top,
          left: gridRef.current.api.getHorizontalPixelRange().left,
        };
      }

      fetchNextPage();
    }, [fetchNextPage]);

    const handleBodyScroll = useCallback(
      (event: BodyScrollEvent<Evaluation>) => {
        const verticalScrollPosition = event.top;
        const bodyHeight =
          gridContainerRef.current?.querySelector('.ag-body')
            ?.clientHeight || 0;
        const viewPortHeight =
          gridContainerRef.current?.querySelector(
            '.ag-viewport.ag-center-cols-viewport',
          )?.scrollHeight || 0;
        const scrollableHeight = viewPortHeight - bodyHeight;
        const isScrollingDown = event.top > lastScrollPosition.current;

        if (
          verticalScrollPosition >= scrollableHeight - 50 &&
          event.direction === 'vertical' &&
          isScrollingDown &&
          hasNextPage
        ) {
          fetchMoreEvaluations();
        }

        lastScrollPosition.current = verticalScrollPosition;
      },
      [fetchMoreEvaluations, hasNextPage],
    );

    useEffect(() => {
      if (!evaluationsData?.pages.length) return;

      if (gridRef.current?.api) {
        setTimeout(() => {
          if (gridRef.current?.api) {
            gridRef.current.api.ensureIndexVisible(
              Math.floor(scrollPositionRef.current.top / 50),
              'top',
            );
          }
        }, 0);
      }
    }, [evaluationsData?.pages.length]);

    const handleDownloadEvaluation = useCallback(
      async (evaluation: Evaluation) => {
        if (!evaluation?.id) return;

        setActiveEvaluationId(evaluation.id);
        setIsExportingCsv(true);

        try {
          const response = await fetchEvaluationExcel(evaluation.id);
          const blob = await response.blob();
          fileDownload(
            blob,
            getEvaluationReportFilename(application?.bitId),
          );
        } catch (error) {
          console.log({ error });
        } finally {
          setActiveEvaluationId(null);
          setIsExportingCsv(false);
        }
      },
      [application?.bitId],
    );

    const evaluationRows = useMemo(() => {
      return getEvaluationRows(evaluationsData?.pages);
    }, [evaluationsData?.pages]);

    const latestEvaluation = evaluationRows[0];
    const hasEvaluationTrendData = evaluationRows.length > 0;

    const evaluationTrendData = useMemo(() => {
      return getEvaluationTrendData(evaluationRows);
    }, [evaluationRows]);

    useEffect(() => {
      if (!isMobileViewport) return;

      const runButton = mobileRunButtonRef.current;
      if (!runButton) return;

      const clearUnexpectedInitialFocus = () => {
        if (document.activeElement === runButton) {
          runButton.blur();
        }
      };

      clearUnexpectedInitialFocus();
      const frameId =
        window.requestAnimationFrame(clearUnexpectedInitialFocus);

      return () => {
        window.cancelAnimationFrame(frameId);
      };
    }, [applicationBitId, isMobileViewport]);

    useEffect(() => {
      if (!isMobileViewport && isMobileDetailsOpen) {
        setIsMobileDetailsOpen(false);
      }
    }, [isMobileDetailsOpen, isMobileViewport]);

    useEffect(() => {
      if (typeof window === 'undefined') return;

      const openMobileDetails = () => {
        if (isMobileViewport) {
          setIsMobileDetailsOpen(true);
        }
      };

      window.addEventListener(
        APPLICATION_DETAILS_OPEN_MOBILE_EVENT,
        openMobileDetails,
      );

      return () => {
        window.removeEventListener(
          APPLICATION_DETAILS_OPEN_MOBILE_EVENT,
          openMobileDetails,
        );
      };
    }, [isMobileViewport]);

    useEffect(() => {
      if (!isMobileViewport) return;

      const sentinel = mobileLoadMoreSentinelRef.current;
      if (!sentinel) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          root: null,
          rootMargin: '200px 0px',
          threshold: 0,
        },
      );

      observer.observe(sentinel);

      return () => {
        observer.disconnect();
      };
    }, [
      evaluationRows.length,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      isMobileViewport,
    ]);

    const handleEnvironmentChange = useCallback(
      (event: ChangeEvent<HTMLSelectElement>) => {
        const environment = event.target.value;
        setSelectedEnvironment(environment);
        navigate(`/applications/${applicationBitId}?env=${environment}`, {
          replace: true,
        });
      },
      [applicationBitId, navigate],
    );

    const handleDesktopEnvironmentChange = useCallback(
      (event: ChangeEvent<HTMLSelectElement>) => {
        setIsMobileDetailsOpen(false);
        handleEnvironmentChange(event);
      },
      [handleEnvironmentChange],
    );

    const handleNavigateBack = useCallback(() => {
      navigate('/applications');
    }, [navigate]);

    const handleNavigateToConfiguration = useCallback(() => {
      navigate(`/onboard?bitId=${applicationBitId}`);
    }, [applicationBitId, navigate]);

    const handleNavigateToConfigurationFromMobile = useCallback(() => {
      setIsMobileDetailsOpen(false);
      handleNavigateToConfiguration();
    }, [handleNavigateToConfiguration]);

    const handleNavigateToEvaluation = useCallback(
      (evaluationId: string) => {
        navigate(`/evaluations/${evaluationId}`);
      },
      [navigate],
    );

    const handleOpenMobileDetails = useCallback(() => {
      setIsMobileDetailsOpen(true);
    }, []);

    const handleCloseMobileDetails = useCallback(() => {
      setIsMobileDetailsOpen(false);
    }, []);

    const handleOpenRunEvaluation = useCallback(() => {
      setIsRunEvaluationOpen(true);
    }, []);

    const handleCloseRunEvaluation = useCallback(() => {
      setIsRunEvaluationOpen(false);
    }, []);

    const runEvaluationApplication =
      useMemo<RunEvaluationApplication | null>(() => {
        return getRunEvaluationApplication({
          isOpen: isRunEvaluationOpen,
          application,
          selectedEnvironment,
        });
      }, [application, isRunEvaluationOpen, selectedEnvironment]);

    return {
      desktopTrendProps: {
        className:
          'w-full flex flex-col gap-4 mt-auto auto-hide min-h-0 max-sm:hidden',
        evaluationTrendData,
        hasEvaluationTrendData,
      },
      fieldsProps: {
        application,
        latestEvaluation,
      },
      gridProps: {
        activeEvaluationId,
        evaluationRows,
        gridContainerRef,
        gridRef,
        isExportingCsv,
        isFetchingNextPage,
        isLoading,
        onBodyScroll: handleBodyScroll,
        onDownloadEvaluation: handleDownloadEvaluation,
        onNavigateToEvaluation: handleNavigateToEvaluation,
      },
      headerProps: {
        applicationName: application?.name,
        isMobileViewport,
        mobileRunButtonRef,
        onBack: handleNavigateBack,
        onOpenMobileDetails: handleOpenMobileDetails,
        onOpenRunEvaluation: handleOpenRunEvaluation,
        selectedEnvironmentAcronym,
        selectedEnvironmentKey,
      },
      isMobileViewport,
      mobileListProps: {
        activeEvaluationId,
        evaluationRows,
        isExportingCsv,
        isFetchingNextPage,
        isLoading,
        loadMoreSentinelRef: mobileLoadMoreSentinelRef,
        onDownloadEvaluation: handleDownloadEvaluation,
        onNavigateToEvaluation: handleNavigateToEvaluation,
      },
      mobilePanelProps: {
        application,
        evaluationTrendData,
        hasEvaluationTrendData,
        isOpen: isMobileDetailsOpen,
        latestEvaluation,
        onClose: handleCloseMobileDetails,
        onEnvironmentChange: handleEnvironmentChange,
        onNavigateToConfiguration:
          handleNavigateToConfigurationFromMobile,
        selectedEnvironment,
      },
      runEvaluationProps: {
        application: runEvaluationApplication,
        isOpen: isRunEvaluationOpen,
        onClose: handleCloseRunEvaluation,
      },
      toolbarProps: {
        onEnvironmentChange: handleDesktopEnvironmentChange,
        onNavigateToConfiguration: handleNavigateToConfiguration,
        onOpenRunEvaluation: handleOpenRunEvaluation,
        selectedEnvironment,
      },
    };
  };
